import React from 'react';
import { ErrorType } from './constants';
import {
  SurveyError,
  SurveyErrorGroup,
  SurveyErrorType,
  SurveyErrorMeta,
  UseSurveyErrorsProps,
  UseSurveyErrorsReturn,
  SurveyErrorMetaGroup,
} from './types';

const defaultErrorGroups: SurveyErrorGroup = {
  redirect: [ErrorType.REDIRECT, ErrorType.COLLECTOR_CLOSED],
  notFound: [ErrorType.FOUR_OH_FOUR],
  validation: [ErrorType.MISSING_REQUIRED_FIELD, ErrorType.INVALID_OPEN_ENDED_VALIDATION],
};

function useSurveyErrors(
  config: UseSurveyErrorsProps = { groups: defaultErrorGroups, meta: {} }
): UseSurveyErrorsReturn {
  const [errors, setErrors] = React.useState<SurveyError[]>([]);

  const errorGroups = config.groups || {};
  let metaConfig = config.meta || {};

  /** Set error metadata */
  const setMeta = (meta: SurveyErrorMetaGroup): void => {
    metaConfig = { ...config.meta, ...meta };
  };

  /** Add an error to state */
  const setError = (error: SurveyError): void => {
    setErrors(errs => [...errs, error]);
  };

  /** Reset all errors assigned to a question  */
  const setErrorsById = (id: string | (string | null)[] | null, newErrors: SurveyError[]): void => {
    const ids = Array.isArray(id) ? id : [id];
    setErrors(errs => {
      const filteredErrs = errs.filter(e => !ids.includes(e.questionId));
      return [...filteredErrs, ...newErrors];
    });
  };

  /** Remove errors by questionId */
  const removeErrorsById = (id: string | (string | null)[] | null): void => {
    const ids = Array.isArray(id) ? id : [id];
    setErrors(errs => {
      const filteredErrs = errs.filter(e => !ids.includes(e.questionId));
      return [...filteredErrs];
    });
  };

  /** Get errors by questionId */
  const getErrorsById = (id: string): SurveyError[] => {
    return errors.filter(e => e.questionId === id);
  };

  /** Get errors by type/code */
  const getErrorsByType = (type: SurveyErrorType | SurveyErrorType[]): SurveyError[] => {
    const types = Array.isArray(type) ? type : [type];
    return errors.filter(e => types.includes(e.code));
  };

  /** Remove errors by type/code */
  const removeErrorsByType = (type: SurveyErrorType | SurveyErrorType[]): void => {
    const types: SurveyErrorType[] = Array.isArray(type) ? type : [type];
    setErrors(errs => {
      const filteredErrs = errs.filter(e => !types.includes(e.code));
      return [...filteredErrs];
    });
  };

  /** Get Meta info from an error */
  const getErrorMetaByType = (type: SurveyErrorType | SurveyErrorType[]): SurveyErrorMeta | null => {
    const errorType = getErrorsByType(type)[0];
    if (errorType) {
      const defaultMeta = metaConfig[errorType.code] ?? {};
      return { ...defaultMeta, ...errorType.meta };
    }
    return null;
  };

  /** reset (clear) errors */
  const reset = (): void => setErrors([]);

  // API
  return {
    errors,
    errorGroups,
    get hasNoErrors(): boolean {
      return !errors.length;
    },
    get hasVersionChanged(): boolean {
      return errors.some(({ code }: SurveyError) => code === ErrorType.INVALID_SURVEY_VERSION);
    },
    get hasNotFoundErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.notFound.includes(code));
    },
    get hasRedirectErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.redirect.includes(code));
    },
    get hasValidationErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.validation.includes(code));
    },
    reset,
    setErrors,
    setError,
    setErrorsById,
    getErrorsByType,
    getErrorMetaByType,
    getErrorsById,
    removeErrorsByType,
    removeErrorsById,
    setMeta,
  } as UseSurveyErrorsReturn;
}

export default useSurveyErrors;
